import * as React from "react";
import { FormErrorList } from "../FormErrorList";
import { IDropdownItem } from "../IDropdownItem";
import { Form, Divider, Message, Container, Segment, FormButton, FormField, FormInput, FormSelect } from "@/components/bp/bp-ui";
import { FileUpload, IUploadedFile } from "../FileUpload";
import { FormikProps, Formik, Field } from "formik";
import * as yup from "yup";
import { TranslationContext } from "../Contexts/TranslationContext";
import { ClientResources, KitPopulationClient, LabClient } from "@app/api2/api";
import SemanticField from "@app/components/SemanticField";
import { PageHeader } from "@common/PageHeader";
import { replaceInString } from "@common/Utils";
import { buildUrl, AppRoutes } from "../AppRoutes";
import { BPContainer } from "@/components/bp/bpcontainer";

interface IPopulationEditorProps {
    newPopulation: boolean;
    populationId: number;
}

class PopulationEditorFormValues {
    public name: string = "";
    public lab: number = 0;
    public file: IUploadedFile;
    public sampledProfilesCount?: number;
}

class PopulationEditorState {
    public isValid: boolean = false;
    public loading: boolean = true;
    public labList: Array<IDropdownItem<number>> = [];
    public singleLab: boolean = false;
    public serverErrors: string[] = null;
    public showServerErrors: boolean = false;
    public formData: PopulationEditorFormValues;
    public initialValues: PopulationEditorFormValues;
}

export class PopulationEditor extends React.Component<IPopulationEditorProps, PopulationEditorState> {

    private createValidationSchema: yup.AnyObjectSchema;
    private editValidationSchema: yup.AnyObjectSchema;

    constructor(props, context) {
        super(props, context);
        
        this.state = new PopulationEditorState();

        this.createValidationSchema = yup.object().shape({
            file: yup.object().shape({
                fileName: yup.string().required(),
            }),
            lab: yup.number().required(window.translation.common.lab),
            name: yup.string().required(window.translation.population.name),
        });

        this.editValidationSchema = yup.object().shape({
            name: yup.string().required(window.translation.population.name),
        });
    }

    public setStateFromPrevious(oldState: PopulationEditorState): void {
        this.setState({ ...oldState, initialValues: oldState.formData });
    }

    public async componentDidMount(): Promise<any> {
        const labClient = new LabClient();
        const populationClient = new KitPopulationClient();

        const labs = await labClient.getAll();

        const mappedLabs = labs.labs.map((l) => ({ key: l.id, value: l.id, text: l.name }));
        let labId = this.state.formData ? this.state.formData.lab : 0;

        const singleLab = mappedLabs.length === 1;
        if (singleLab) {
            labId = mappedLabs[0].key;
        }

        this.setState({
            formData: {
                ... this.state.formData,
                lab: labId,
            },
            initialValues: {
                ...this.state.formData,
                lab: labId,
            },
            labList: mappedLabs,
            singleLab,
            loading: false,
        });

        if (!this.props.newPopulation) {
            const population = await populationClient.get(this.props.populationId);
            this.setState({
                initialValues: {
                    ...this.state.formData,
                    name: population.population.name,
                    sampledProfilesCount: population.population.sampledProfilesCount
                },
            });
        }
    }

    public render() {
        return <BPContainer>
            <PageHeader header={this.props.newPopulation ? window.translation.populationView.upload : replaceInString(window.translation.populationView.edit, { '{populationName}': this.state?.initialValues?.name })} />
            <Segment>
                <Formik initialValues={{ ...this.state.initialValues }} validationSchema={this.props.newPopulation ? this.createValidationSchema : this.editValidationSchema} isInitialValid={!this.props.newPopulation} enableReinitialize onSubmit={this.submit}>
                    {(props) => {
                        return this.renderForm(props)
                    }}
                </Formik>
            </Segment>
        </BPContainer>;
    }

    submit = async (data: PopulationEditorFormValues): Promise<any> => {
        // console.log(data);
        let response = null;
        const populationClient = new KitPopulationClient();
        this.setState({ loading: true });
        if (this.props.newPopulation) {
            response = await populationClient.create({
                fileContents: data.file.contents,
                labId: data.lab,
                name: data.name,
                sampledProfilesCount: data.sampledProfilesCount ? data.sampledProfilesCount : null
            });
        } else {
            response = await populationClient.update(this.props.populationId, {
                // active: true,
                // fileContents: data.file ? data.file.contents : null,
                id: this.props.populationId,
                name: data.name,
                sampledProfilesCount: data.sampledProfilesCount ? data.sampledProfilesCount : null
            });
        }

        // console.log(response)

        if (response.success) {
            window.location.assign(buildUrl(AppRoutes.PopulationDetail, {populationId: this.props.newPopulation ? response.id : this.props.populationId}));
            // window.location.assign(response.url);
        } else {
            this.setState({
                loading: false,
                serverErrors: response.errors,
                showServerErrors: true,
            });
        }
    }

    private renderForm = (props: FormikProps<PopulationEditorFormValues>) => {
        const handleChange = async (name: string, data: IUploadedFile) => {
            // if (data && data.name) {
                await props.setFieldTouched(name);
                await props.setFieldValue(name, data);
                this.setState({ formData: { ...this.state.formData, [name]: data } });
            // }
        };

        const errorList = Object.getOwnPropertyNames(props.errors)
            .filter((k) => props.touched[k])
            .map((k) => props.errors[k]);

        return (<TranslationContext.Consumer>{(t) =>
            <Form onSubmit={props.handleSubmit} loading={this.state.loading}>
                <SemanticField component={FormInput} required width={6} name="name" label={t.population.name} value={props.values.name} error={props.errors.name && props.touched.name} />
                {(!this.state.singleLab && this.props.newPopulation) &&
                    <SemanticField component={FormSelect} required width={6} name="lab" label={t.common.lab} options={this.state.labList} value={props.values.lab} />}
                <FormField required={this.props.newPopulation} width={6}>
                    <label>{t.population.populationFile}</label>
                    <FileUpload placeholderText={t.population.populationFile} name="file" fileUploaded={handleChange} value={props.values.file} />
                    {!this.props.newPopulation && <div className="ui pointing label">
                        {t.population.noUploadUnchanged}
                    </div>}
                </FormField>
                <SemanticField component={FormInput} width={6} name="sampledProfilesCount" label={t.population.sampledProfilesCount} value={props.values.sampledProfilesCount} error={props.errors.name && props.touched.name} />

                <Divider />
                <FormButton disabled={!props.isValid}>{t.common.save}</FormButton>
                <Message error header={t.common.errorsOnForm} list={errorList} visible={errorList.length > 0} />
                <FormErrorList message={this.props.newPopulation ? t.population.errorCreating : t.population.errorUpdating} show={this.state.showServerErrors} errors={this.state.serverErrors} />
            </Form>}</TranslationContext.Consumer>);
    }
}
